import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';
import { useClient } from '../../apollo';
import { requestInit, RequestState } from '../../auth/hooks/useRequestState';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import {
  ActivitiesFromReport,
  activitiesFromReport,
  ForecastFieldProps,
  getHideFinalForecastField,
  getHideForecastField,
  getReport,
  getReportId,
  getReportIdByPathname,
  getReportState,
  ReportBuilderData,
  unableToLoadReportErrorMessage,
} from './utils';
import TrueImpactDebug from 'npo/pages/ReportBuilder/TrueImpactDebug';
import { useLocation } from 'react-router';
import {
  IndicatorDetails,
  isReportPassed,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import {
  isReportFinalized,
  isReportInterim,
} from 'npo/pages/ReportBuilder/Components/ReportFinalize/utils';

const getReportPassed = (report: ReportBuilderData | null) => {
  if (!report || !report.year) return false;
  return (
    report.stage === 'FINAL' ||
    report.stage === 'INTERIM' ||
    isReportPassed(report.endDate, Number(report.year), report.stage)
  );
};

interface ReportContextProps {
  activities: ActivitiesFromReport;
  report: RequestState<ReportBuilderData>;
  isThirdParty: boolean;
  isLegacyReport: boolean;
  isInterimStage: boolean;
  isFinalStage: boolean;
  forecastField: (i: IndicatorDetails) => ForecastFieldProps;
  finalForecastField: (i: IndicatorDetails) => ForecastFieldProps;
  forecastKey: 'forecast' | 'finalForecast';
  getForecastValue: (i: IndicatorDetails) => IndicatorDetails['forecast'];
  totalAllocationAmount: number;
  isReportEndDatePassed: boolean;
  programName: string | undefined;
  handleReloadReport: () => unknown;
  loadReport: (id: number | string | undefined) => unknown;
  handleReportDate: (start: Date, end: Date) => unknown;
}

export const ReportContext = createContext<ReportContextProps>({
  report: requestInit(),
  activities: {
    serviceDelivery: '',
    serviceSubType: '',
    serviceSecondarySubType: '',
    averageValueOfGoods: '',
    frequencyOfGoods: '',
    durationOfGoods: '',
    primaryInterventionDescription: '',
  },
  forecastField: () => ({
    hide: false,
    disabled: false,
    label: 'Forecast',
    placeholder: 'Forecast',
  }),
  finalForecastField: () => ({
    hide: true,
    disabled: false,
    label: 'Final Results',
    placeholder: 'Final Results',
  }),
  forecastKey: 'forecast',
  getForecastValue: () => undefined,
  isThirdParty: false,
  isLegacyReport: false,
  isInterimStage: false,
  isFinalStage: false,
  programName: undefined,
  totalAllocationAmount: 0,
  isReportEndDatePassed: false,
  handleReloadReport: () => { /* noop */ },
  loadReport: () => { /* noop */ },
  handleReportDate: () => { /* noop */ },
});

export const useReport = () => useContext(ReportContext);

const ReportProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const client = useClient();
  const { pathname } = useLocation();
  const [debug, setDebug] = useState<boolean>(false);
  const [report, makeRequest, setReport] = useGraphQLRequest<{
    allReports: ReportBuilderData[];
  }>();
  const [reportId, setReportId] = useState<number | string | undefined>(
    getReportId(pathname),
  );

  useEffect(() => {
    const newReportId = getReportIdByPathname(pathname);
    if (newReportId && newReportId !== reportId) {
      setReportId(newReportId);
    }
  }, [pathname]);

  const handleReportApiCall = useCallback(
    (id: number | string | undefined = reportId) => {
      if (!client || !id) return;
      makeRequest(getReport(client, id));
    },
    [client, makeRequest, reportId],
  );

  const handleReportDate = useCallback(
    (start: Date, end: Date) => {
      if (!reportId) return;
      const startDate = start.toISOString().slice(0, 10);
      const endDate = end.toISOString().slice(0, 10);

      setReport(s => {
        if (s.type !== 'REQUEST_SUCCESS' || !s.data?.allReports?.length) {
          return s;
        }

        const idAsNumber =
          typeof reportId === 'string' ? Number(reportId) : reportId;
        const nextAllReports = s.data.allReports.map(r =>
          r.dbId === idAsNumber
            ? {
                ...r,
                startDate,
                endDate,
                year: start.getFullYear().toString(),
              }
            : r,
        );

        return {
          ...s,
          data: {
            ...s.data,
            allReports: nextAllReports,
          },
        };
      });
    },
    [reportId],
  );

  useEffect(() => {
    handleReportApiCall();
  }, [handleReportApiCall]);

  const loadReport = useCallback(
    (id: number | string | undefined) => {
      if (!id) return;
      setReportId(id);
    },
    [client, makeRequest],
  );

  const handleReloadReport = useCallback(() => {
    if (!reportId) return;
    handleReportApiCall(reportId);
  }, [reportId]);

  if (report.type === 'REQUEST_ERROR') {
    return (
      <TrueImpactText color="critical" align="center">
        {unableToLoadReportErrorMessage}
      </TrueImpactText>
    );
  }

  const reportData = getReportState(report);
  const r = reportData.data;
  const isReportPassed = getReportPassed(r);
  const forecastKey: 'forecast' | 'finalForecast' = isReportPassed
    ? 'finalForecast'
    : 'forecast';
  const activities = activitiesFromReport(r);

  return (
    <ReportContext.Provider
      value={{
        loadReport,
        handleReloadReport,
        handleReportDate,
        activities,
        isInterimStage: r?.stage ? isReportInterim(r?.stage) : false,
        isFinalStage: r?.stage ? isReportFinalized(r?.stage) : false,
        totalAllocationAmount: r?.totalDonations || 0,
        isReportEndDatePassed: isReportPassed,
        isLegacyReport: r?.version !== 'v4',
        report: reportData,
        forecastField: i => getHideForecastField(r, i, isReportPassed),
        finalForecastField: i =>
          getHideFinalForecastField(r, i, isReportPassed),
        forecastKey,
        getForecastValue: i => i[forecastKey],
        isThirdParty: Boolean(r?.cnOptedIn),
        programName: reportData.data?.programName,
      }}
    >
      <TrueImpactDebug
        isDebug={debug}
        handleChange={setDebug}
        debugText="Debug Report Mode"
        letterKey="P"
        onClick={() => console.log(r)}
        backgroundColor="criticalHover"
      />
      {children}
    </ReportContext.Provider>
  );
};

export default ReportProvider;
